import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import cn from 'classnames'

import styles from './styles.module.scss'

import { getCompanyAssets } from '../../../api'
import { useQuery } from '../../../hooks/useQuery'

import AssetRow from '../AssetRow'
import AssetMap from '../AssetMap'
import Input from '../../Core/Input/Input'
import AssetFilters from '../Assets/AssetFilters'

const AssetsLocations = ({companyId}) => {
  const { t } = useTranslation()
  const { asset_id } = useQuery(['asset_id'])
  const history = useHistory()

  const [assets, setAssets] = useState([])
  const [filters, setFilters] = useState({
    category: 'all',
    ownership: 'all',
    status: 'all',
    assigned: 'all',
    place: 'all',
    supplier: 'all',
    maintenance: 'all',
    scanAge: 'all',
  })

  const selectedAsset = assets.find(asset => asset.id === asset_id)

  const scanAgeOptions = [
    { id: 'all', name: t('All') },
    { id: 'never', name: t('Never scanned') },
    { id: 'recently', name: t('Recently scanned') },
    { id: '7_days', name: t('7 days') },
    { id: '30_days', name: t('30 days') },
    { id: '90_days', name: t('90 days') },
  ]

  const filteredAssets = assets.filter(asset => {
    if (filters.search && !asset.name.toLowerCase().includes(filters.search.toLowerCase())) return false
    if (filters.category !== 'all' && asset.category_id !== filters.category) return false
    if (filters.ownership !== 'all' && asset.ownership_id !== filters.ownership) return false
    if (filters.status !== 'all' && asset.status !== filters.status) return false
    if (filters.assigned === 'assigned' && !asset.employee) return false
    if (filters.assigned === 'unassigned' && asset.employee) return false
    if (filters.place !== 'all' && (!asset.place || asset.place.id !== filters.place)) return false
    if (filters.scanAge !== 'all') {
      const now = new Date()
      const lastScan = asset.latest_scan ? new Date(asset.latest_scan.created_at) : null
      if (filters.scanAge === 'never' && lastScan) return false
      if (filters.scanAge === 'recently' && (!lastScan || (now - lastScan) > 7 * 24 * 60 * 60 * 1000)) return false
      if (filters.scanAge === '7_days' && (!lastScan || (now - lastScan) > 7 * 24 * 60 * 60 * 1000)) return false
      if (filters.scanAge === '30_days' && (!lastScan || (now - lastScan) > 30 * 24 * 60 * 60 * 1000)) return false
      if (filters.scanAge === '90_days' && (!lastScan || (now - lastScan) > 90 * 24 * 60 * 60 * 1000)) return false
    }
    return true
  })

  useEffect(() => {
    getAssets()
  }, [companyId])

  const getAssets = async () => {
    const query = {
      last_scan: true,
      employee: true,
      place: true,
    }
    const res = await getCompanyAssets(companyId, query)
    if (Array.isArray(res)) {
      setAssets(res)
    }
  }

  const handleChange = e => {
    const { name, value } = e.target
    setFilters(prev => ({...prev, [name]: value}))
  }

  const handleSelectAsset = (asset) => {
    history.push({search: `?tab=locations${asset.id === selectedAsset?.id ? '' : `&asset_id=${asset.id}`}`})
  }

  return (
    <div className={styles.container}>
      <div className={styles.filters}>
        <AssetFilters
          companyId={companyId}
          filters={filters}
          sections={{
            category: true,
            ownership: true,
            status: true,
            assigned: true,
            place: true,
          }}
          onChange={val => setFilters(prev => ({...prev, ...val}))} />
        <div className={styles.scanOptions}>
          {
            scanAgeOptions.map((option) => {
              const isActive = filters.scanAge === option.id
              return (
                <button type="button" className={cn(styles.scanOption, {[styles.active]: isActive})} key={option.id} onClick={() => setFilters(prev => ({...prev, scanAge: option.id}))}>
                  { option.name }
                </button>
              )
            })
          }
        </div>
      </div>
      <div className={styles.content}>
        <div className={styles.list}>
          <div className={styles.search}>
            <Input
              id='search'
              name='search'
              type='text'
              value={filters.search}
              onChange={handleChange}
              placeholder={t('Search by name')}
              fullWidth
            />
          </div>
          {
            filteredAssets.map(asset => {
              return (
                <AssetRow
                  key={asset.id}
                  asset={asset}
                  isSelected={selectedAsset?.id === asset.id}
                  onSelect={handleSelectAsset} />
              )
            })
          }
        </div>
        <div className={styles.map}>
          <AssetMap
            selectedAsset={selectedAsset}
            assets={filteredAssets} />
        </div>
      </div>
    </div>
  )
}

export default AssetsLocations
